import { useMemo, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Plus, Search } from 'lucide-react'

import type { RoleSummary } from '@/lib/api/role-api'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { CreateRoleDialog } from '@/components/roles/create-role-dialog'
import { DeleteRoleDialog } from '@/components/roles/delete-role-dialog'
import { EditRoleDialog } from '@/components/roles/edit-role-dialog'
import { RoleActionsMenu } from '@/components/roles/role-actions-menu'
import { useAuth } from '@/hooks/use-auth'
import { useRoleSummaries, useUpdateRole } from '@/hooks/use-role-query'
import { m } from '@/lib/paraglide/messages'
import { twJoin } from '@/lib/utils'

export const Route = createFileRoute('/_app/_authed/_sidebar/settings/roles/')({
    component: RolesPage,
})

type KindFilter = 'all' | 'system' | 'custom'

const KIND_FILTERS: { id: KindFilter; label: () => string }[] = [
    { id: 'all', label: () => m.roles_filter_all() },
    { id: 'system', label: () => m.roles_filter_system() },
    { id: 'custom', label: () => m.roles_filter_custom() },
]

function RolesPage() {
    const navigate = useNavigate()
    const { hasPermission, isAdmin } = useAuth()
    // Route `_authed` chỉ kiểm tra đã đăng nhập, nên màn tự chặn: gõ thẳng URL
    // mà không có roles.read thì thấy lời từ chối, không phải một bảng trống
    // kèm 403 trong console.
    const canRead = hasPermission('roles.read')
    // Sửa vai trò/phân quyền là đặc quyền của VAI TRÒ admin, không phải của mã
    // quyền: ai giữ `permissions.assign` có thể tự cấp cho mình mọi quyền.
    // Backend chặn ở `usecase/role.requireAdmin`; đây chỉ là lớp hiển thị.
    const canManage = isAdmin
    const { data: roles = [], isLoading } = useRoleSummaries({
        enabled: canRead,
    })

    const [search, setSearch] = useState('')
    const [kind, setKind] = useState<KindFilter>('all')
    const [createOpen, setCreateOpen] = useState(false)
    const [copyFromId, setCopyFromId] = useState<string | undefined>()
    const [editing, setEditing] = useState<RoleSummary | null>(null)
    const [deleting, setDeleting] = useState<RoleSummary | null>(null)

    const filtered = useMemo(() => {
        const needle = search.trim().toLowerCase()
        return roles.filter((role) => {
            if (kind === 'system' && !role.is_system) return false
            if (kind === 'custom' && role.is_system) return false
            if (!needle) return true
            return (
                role.name.toLowerCase().includes(needle) ||
                role.code.toLowerCase().includes(needle)
            )
        })
    }, [roles, search, kind])

    const totals = useMemo(
        () => ({
            roleCount: roles.length,
            // Tổng quyền của hệ thống = số quyền của vai trò giữ nhiều nhất.
            // `admin` bị khoá ở mức toàn quyền (migration
            // 20260906070052 cấp bù đủ), nên đây luôn là con số đầy đủ mà
            // không cần thêm quyền `permissions.read` chỉ để hiện dòng tóm tắt.
            permissionCount: roles.reduce(
                (max, r) => Math.max(max, r.permission_count),
                0,
            ),
            userCount: roles.reduce((sum, r) => sum + r.user_count, 0),
        }),
        [roles],
    )

    const openPermissions = (role: RoleSummary) =>
        void navigate({
            to: '/settings/roles/$id',
            params: { id: role.id },
        })

    if (!canRead) {
        return (
            <div className="py-12 text-center text-muted-foreground">
                {m.common_permission_denied()}
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-4 page-pad">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h1 className="text-xl font-semibold">{m.roles_title()}</h1>
                    <p className="mt-1 text-sm tabular-nums text-muted-foreground">
                        {m.roles_subtitle(totals)}
                    </p>
                </div>
                {canManage && (
                    <Button
                        onClick={() => {
                            setCopyFromId(undefined)
                            setCreateOpen(true)
                        }}
                    >
                        <Plus className="size-4" />
                        {m.roles_create()}
                    </Button>
                )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
                <div className="relative min-w-48 flex-1 sm:max-w-sm">
                    <Search className="pointer-events-none absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={m.roles_search_placeholder()}
                        className="pl-8"
                    />
                </div>
                <div className="inline-flex rounded-md bg-muted p-0.5">
                    {KIND_FILTERS.map((f) => (
                        <button
                            key={f.id}
                            type="button"
                            aria-pressed={kind === f.id}
                            onClick={() => setKind(f.id)}
                            className={twJoin(
                                'rounded px-3 py-1.5 text-sm font-medium transition-colors',
                                kind === f.id
                                    ? 'bg-background text-foreground shadow-sm'
                                    : 'text-muted-foreground hover:text-foreground',
                            )}
                        >
                            {f.label()}
                        </button>
                    ))}
                </div>
            </div>

            <div className="overflow-x-auto rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.roles_col_role()}</TableHead>
                            <TableHead>{m.roles_col_description()}</TableHead>
                            <TableHead>{m.roles_col_kind()}</TableHead>
                            <TableHead className="text-right">
                                {m.roles_col_permissions()}
                            </TableHead>
                            <TableHead className="text-right">
                                {m.roles_col_users()}
                            </TableHead>
                            <TableHead>{m.roles_col_status()}</TableHead>
                            <TableHead />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {filtered.length === 0 && !isLoading && (
                            <TableRow>
                                <TableCell
                                    colSpan={7}
                                    className="py-10 text-center text-sm text-muted-foreground"
                                >
                                    {m.roles_empty()}
                                </TableCell>
                            </TableRow>
                        )}
                        {filtered.map((role) => (
                            <TableRow key={role.id}>
                                <TableCell>
                                    <span className="flex flex-col">
                                        <span className="font-medium">
                                            {role.name}
                                        </span>
                                        <span className="font-mono text-xs text-muted-foreground">
                                            {role.code}
                                        </span>
                                    </span>
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                    {role.description}
                                </TableCell>
                                <TableCell>
                                    <Badge
                                        variant={
                                            role.is_system
                                                ? 'outline'
                                                : 'secondary'
                                        }
                                    >
                                        {role.is_system
                                            ? m.roles_kind_system()
                                            : m.roles_kind_custom()}
                                    </Badge>
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                    <button
                                        type="button"
                                        className="font-medium text-primary underline-offset-4 hover:underline"
                                        onClick={() => openPermissions(role)}
                                    >
                                        {role.permission_count}
                                    </button>
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                    {role.user_count}
                                </TableCell>
                                <TableCell>
                                    <RoleStatusBadge role={role} />
                                </TableCell>
                                <TableCell className="text-right">
                                    {canManage && (
                                        <RoleRowActions
                                            role={role}
                                            onPermissions={() =>
                                                openPermissions(role)
                                            }
                                            onRename={() => setEditing(role)}
                                            onDuplicate={() => {
                                                setCopyFromId(role.id)
                                                setCreateOpen(true)
                                            }}
                                            onDelete={() => setDeleting(role)}
                                        />
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            <CreateRoleDialog
                open={createOpen}
                onOpenChange={setCreateOpen}
                roles={roles}
                copyFromRoleId={copyFromId}
                onCreated={(id) =>
                    void navigate({
                        to: '/settings/roles/$id',
                        params: { id },
                    })
                }
            />
            <EditRoleDialog
                role={editing}
                open={editing !== null}
                onOpenChange={(open) => !open && setEditing(null)}
            />
            <DeleteRoleDialog
                role={deleting}
                open={deleting !== null}
                onOpenChange={(open) => !open && setDeleting(null)}
            />
        </div>
    )
}

function RoleStatusBadge({ role }: { role: RoleSummary }) {
    if (role.is_locked)
        return <Badge variant="outline">{m.roles_status_locked()}</Badge>
    return (
        <Badge variant={role.is_active ? 'secondary' : 'outline'}>
            {role.is_active
                ? m.roles_status_active()
                : m.roles_status_inactive()}
        </Badge>
    )
}

interface RowActionProps {
    role: RoleSummary
    onPermissions: () => void
    onRename: () => void
    onDuplicate: () => void
    onDelete: () => void
}

function RoleRowActions({
    role,
    onPermissions,
    onRename,
    onDuplicate,
    onDelete,
}: RowActionProps) {
    const updateRole = useUpdateRole(role.id)
    return (
        <RoleActionsMenu
            role={role}
            onPermissions={onPermissions}
            onRename={onRename}
            onDuplicate={onDuplicate}
            onDelete={onDelete}
            onToggleActive={() => {
                updateRole.mutate({ is_active: !role.is_active })
            }}
        />
    )
}
